import { defineStore } from '/src/core/store';

// crypto.randomUUID needs WebKit 15.4+; kiosks on iOS 14 (Podfile minimum) lack it
function generateUid () {
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
		return crypto.randomUUID();
	}

	return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export const useClockStore = defineStore('clocks', {
	state: () => ({
		clocks: [],
	}),
	persist: {
		storage: 'native',
		pick: ['clocks'],
		migrate: 'local',
	},
	getters: {
		pendingClocks () {
			return this.clocks.filter(clock => (clock.status ?? 'pending') === 'pending');
		},
		failedClocks () {
			return this.clocks.filter(clock => clock.status === 'failed');
		},
	},
	actions: {
		setClocks (clocks) {
			this.clocks = clocks;
		},
		addClock (clock) {
			this.clocks = [...this.clocks, {
				...clock,
				uid: clock.uid ?? generateUid(),
				status: 'pending',
				attempts: 0,
				nextRetryAt: 0,
				lastError: null,
				failedAt: null,
			}];
		},
		updateClock (uid, patch) {
			if (uid == null) {
				return;
			}

			this.clocks = this.clocks.map(clock =>
				clock.uid === uid ? { ...clock, ...patch } : clock,
			);
		},
		removeClock (clock) {
			if (clock?.uid) {
				this.clocks = this.clocks.filter(item => item.uid !== clock.uid);
				return;
			}

			// legacy fallback: clocks persisted before uid existed
			const index = this.clocks.indexOf(clock);
			if (index >= 0) {
				const clocks = [...this.clocks];
				clocks.splice(index, 1);

				this.clocks = clocks;
			}
		},
		markClockFailed (uid, error) {
			this.updateClock(uid, {
				status: 'failed',
				failedAt: Date.now(),
				lastError: String(error?.message ?? error ?? 'unknown'),
			});
		},
		normalizeClocks () {
			this.clocks = this.clocks.map(clock => {
				const normalized = {
					status: 'pending',
					attempts: 0,
					nextRetryAt: 0,
					lastError: null,
					failedAt: null,
					...clock,
				};

				// after the spread so an explicit `uid: undefined` can't survive
				normalized.uid = normalized.uid ?? generateUid();

				return normalized;
			});
		},
		reset () {
			this.setClocks([]);
		},
	},
});
